import { Injectable, OnDestroy, inject } from "@angular/core";
import { Store } from "@ngrx/store";
import { Observable, Subscription } from "rxjs";
import { filter, first, map, withLatestFrom } from "rxjs/operators";

import { fetchMenu } from "../actions/menu.action";
import { getCategory } from "../helpers/categories.helper";
import { FlatCategories } from "../model/menu.model";
import { State } from "../reducers/menu.reducer";
import {
    menuState,
    selectAllCategories,
    selectCategories,
    selectLinks,
    selectMenuEntry,
} from "../selectors/menu.selector";

@Injectable()
export class MenuFacade implements OnDestroy {
    private store = inject<Store<State>>(Store);

    public menuEntry$ = this.store.select(selectMenuEntry);
    public categories$ = this.store.select(selectCategories);
    public allCategories$ = this.store.select(selectAllCategories);

    public categoriesPaths$ = this.store.select(selectCategories).pipe(
        map((categories: FlatCategories | undefined) => {
            if (!categories) {
                return [];
            }
            const paths: string[] = [];
            Object.entries(categories).forEach(([_pimCode, categoryEntry]) => {
                paths.push(categoryEntry?.categoryPath);
            });
            return paths;
        }),
    );
    public links$ = this.store.select(selectLinks);

    private subscription = new Subscription();

    public isFinalCategory = (pimCode: string): Observable<boolean> =>
        this.store.select(selectCategories).pipe(
            map((categories: FlatCategories | undefined) =>
                categories ? getCategory(categories, pimCode) : categories,
            ),
            withLatestFrom(this.categoriesPaths$),
            map(
                ([category, categoriesPaths]) =>
                    !!category &&
                    categoriesPaths.filter((path) =>
                        path.includes(category.categoryPath as string),
                    ).length === 1,
            ),
        );

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /**
     * Trigger fetchMenu action if there is no menu data in state or pending menu
     */
    public fetchMenu(): void {
        this.subscription.add(
            this.store
                .select(menuState)
                .pipe(
                    first(),
                    filter((state) => !state.pending && !state.entry),
                )
                .subscribe(() => this.store.dispatch(fetchMenu())),
        );
    }
}
